import type { FileValidationOptions, FileValidationResult } from "@/types";

const DEFAULT_MAX_SIZE = 50 * 1024 * 1024; // 50MB
const DEFAULT_ALLOWED_TYPES = [
	"application/pdf",
	"application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
	"text/plain",
	"application/rtf",
	"text/markdown",
];

const FILE_EXTENSIONS: Record<string, string[]> = {
	"application/pdf": [".pdf"],
	"application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
	"text/plain": [".txt"],
	"application/rtf": [".rtf"],
	"text/markdown": [".md", ".markdown"],
};

/**
 * Validate a file before upload
 */
export const validateFile = (file: File, options?: FileValidationOptions): FileValidationResult => {
	const errors: string[] = [];
	const maxSize = options?.maxSize || DEFAULT_MAX_SIZE;
	const allowedTypes = options?.allowedTypes || DEFAULT_ALLOWED_TYPES;

	// Check file size
	if (file.size > maxSize) {
		errors.push(`File size exceeds maximum allowed size of ${formatFileSize(maxSize)}`);
	}

	// Check file type
	if (!allowedTypes.includes(file.type)) {
		const allowedExtensions = allowedTypes.flatMap((type) => FILE_EXTENSIONS[type] || []).join(", ");
		errors.push(`File type not supported. Allowed types: ${allowedExtensions}`);
	}

	// Check if file is empty
	if (file.size === 0) {
		errors.push("File is empty");
	}

	return {
		valid: errors.length === 0,
		errors,
	};
};

/**
 * Format file size in human-readable format
 */
export const formatFileSize = (bytes: number): string => {
	if (bytes === 0) return "0 Bytes";

	const k = 1024;
	const sizes = ["Bytes", "KB", "MB", "GB"];
	const i = Math.floor(Math.log(bytes) / Math.log(k));

	return `${parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`;
};

/**
 * Get file extension from filename
 */
export const getFileExtension = (filename: string): string => {
	const parts = filename.split(".");
	return parts.length > 1 ? `.${parts[parts.length - 1]}` : "";
};
